import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Modal,
  ModalBody,
  ModalHeader
} from "shards-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRobot,
  faLightbulb,
  faChartPie,
  faBalanceScale,
  faCheckCircle,
  faExclamationTriangle
} from "@fortawesome/free-solid-svg-icons";
import PageTitle from "../../components/common/PageTitle";

const DisputeAssistantRequest = () => {
  const [showRequestForm, setShowRequestForm] = useState(false);

  // Add script for ClickUp form integration
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://app-cdn.clickup.com/assets/js/forms-embed/v1.js';
    script.async = true;
    
    // Add script to document only when the form is shown
    if (showRequestForm) {
      document.body.appendChild(script);
      
      return () => {
        // Clean up - remove script when component unmounts or modal closes
        document.body.removeChild(script);
      };
    }
  }, [showRequestForm]);

  const toggleRequestForm = () => {
    setShowRequestForm(!showRequestForm);
  };

  return (
    <Container fluid className="main-content-container px-4">
      <Row noGutters className="page-header py-4">
        <PageTitle
          title="Dispute Assistant"
          subtitle="Powered by AI"
          md="12"
          className="ml-sm-auto mr-sm-auto"
        />
      </Row>

      <Row>
        <Col lg="12" className="mb-4">
          <Card small className="mb-4">
            <CardBody className="p-0">
              <Row className="py-5">
                <Col md="6" className="d-flex align-items-center justify-content-center">
                  <div className="text-center px-4">
                    <div style={{ 
                      width: "120px", 
                      height: "120px", 
                      borderRadius: "50%", 
                      backgroundColor: "rgba(33, 170, 115, 0.1)", 
                      display: "flex", 
                      alignItems: "center", 
                      justifyContent: "center",
                      margin: "0 auto 24px auto" 
                    }}>
                      <FontAwesomeIcon icon={faRobot} style={{ fontSize: "48px", color: "#21AA73" }} />
                    </div>
                    <h3 className="mb-4">Dispute Assistant</h3>
                    <p className="text-muted">AI-powered tool for intelligent dispute resolution</p>
                  </div>
                </Col>
                <Col md="6" className="p-4">
                  <div className="mb-5">
                    <h4 className="mb-3">Unlock the power of AI-powered dispute resolution</h4>
                    <p>
                      The Dispute Assistant uses advanced artificial intelligence to help you
                      save time and improve accuracy when processing customer disputes.
                    </p>
                    <p>
                      This premium feature is not currently enabled for your account. Request
                      access today to start experiencing the benefits!
                    </p>
                  </div>
                  <Button theme="success" size="lg" onClick={toggleRequestForm}>
                    Request Access
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col lg="4" md="6" className="mb-4">
          <Card small className="h-100">
            <CardBody className="d-flex flex-column">
              <div className="mb-3 text-center">
                <FontAwesomeIcon 
                  icon={faLightbulb} 
                  style={{ 
                    fontSize: "32px", 
                    color: "#F9A43F",
                    padding: "16px",
                    borderRadius: "50%",
                    backgroundColor: "rgba(249, 164, 63, 0.1)",
                    marginBottom: "16px"
                  }} 
                />
                <h5>Smart Recommendations</h5>
              </div>
              <p className="text-muted flex-grow-1">
                Get AI-powered recommendations for how to handle each dispute,
                with confidence scores and rationale.
              </p>
            </CardBody>
          </Card>
        </Col>

        <Col lg="4" md="6" className="mb-4">
          <Card small className="h-100">
            <CardBody className="d-flex flex-column">
              <div className="mb-3 text-center">
                <FontAwesomeIcon 
                  icon={faChartPie} 
                  style={{ 
                    fontSize: "32px", 
                    color: "#3498db",
                    padding: "16px",
                    borderRadius: "50%",
                    backgroundColor: "rgba(52, 152, 219, 0.1)",
                    marginBottom: "16px"
                  }} 
                />
                <h5>Data-Driven Insights</h5>
              </div>
              <p className="text-muted flex-grow-1">
                Analyze historical dispute patterns and identify trends to help
                improve your parking operations.
              </p>
            </CardBody>
          </Card>
        </Col>

        <Col lg="4" md="12" className="mb-4">
          <Card small className="h-100">
            <CardBody className="d-flex flex-column">
              <div className="mb-3 text-center">
                <FontAwesomeIcon 
                  icon={faBalanceScale} 
                  style={{ 
                    fontSize: "32px", 
                    color: "#21AA73",
                    padding: "16px",
                    borderRadius: "50%",
                    backgroundColor: "rgba(33, 170, 115, 0.1)",
                    marginBottom: "16px"
                  }} 
                />
                <h5>Automated Resolutions</h5>
              </div>
              <p className="text-muted flex-grow-1">
                Let AI handle routine disputes automatically based on your criteria,
                reducing manual work by up to 80%.
              </p>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col lg="12" className="mb-4">
          <Card small>
            <CardBody>
              <h4 className="mb-3">How It Works</h4>
              <div className="d-flex mb-3 align-items-start">
                <div 
                  style={{ 
                    minWidth: "36px", 
                    height: "36px", 
                    borderRadius: "50%", 
                    backgroundColor: "rgba(52, 152, 219, 0.1)", 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "center",
                    marginRight: "16px",
                    marginTop: "4px"
                  }}
                >
                  <span style={{ color: "#3498db", fontWeight: "bold" }}>1</span>
                </div>
                <div>
                  <h5>Intelligent Analysis</h5>
                  <p className="text-muted">
                    The AI examines each dispute's text, customer history, evidence provided,
                    and compares it with similar past cases to determine validity.
                  </p>
                </div>
              </div>

              <div className="d-flex mb-3 align-items-start">
                <div 
                  style={{ 
                    minWidth: "36px", 
                    height: "36px", 
                    borderRadius: "50%", 
                    backgroundColor: "rgba(52, 152, 219, 0.1)", 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "center",
                    marginRight: "16px",
                    marginTop: "4px"
                  }}
                >
                  <span style={{ color: "#3498db", fontWeight: "bold" }}>2</span>
                </div>
                <div>
                  <h5>Confidence Scores</h5>
                  <p className="text-muted">
                    Each recommendation comes with a confidence score and detailed explanation
                    of why the AI suggests accepting, rejecting, or adjusting the dispute.
                  </p>
                </div>
              </div>

              <div className="d-flex mb-3 align-items-start">
                <div 
                  style={{ 
                    minWidth: "36px", 
                    height: "36px", 
                    borderRadius: "50%", 
                    backgroundColor: "rgba(52, 152, 219, 0.1)", 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "center",
                    marginRight: "16px",
                    marginTop: "4px"
                  }}
                >
                  <span style={{ color: "#3498db", fontWeight: "bold" }}>3</span>
                </div>
                <div>
                  <h5>Time-Saving Automation</h5>
                  <p className="text-muted">
                    Set confidence thresholds for automated resolutions, freeing your team
                    to focus on more complex cases that require human judgment.
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col lg="6" className="mb-4">
          <Card small className="h-100">
            <CardBody>
              <div className="d-flex align-items-center mb-3">
                <FontAwesomeIcon 
                  icon={faCheckCircle} 
                  style={{ 
                    fontSize: "24px", 
                    color: "#21AA73", 
                    marginRight: "12px" 
                  }} 
                />
                <h4 className="mb-0">Benefits</h4>
              </div>
              <ul className="pl-4">
                <li className="mb-2">Save up to 80% of time spent on dispute processing</li>
                <li className="mb-2">Improve consistency in dispute resolution decisions</li>
                <li className="mb-2">Identify patterns of fraudulent disputes</li>
                <li className="mb-2">Reduce workload for your support team</li>
                <li className="mb-2">Access detailed analytics on dispute trends</li>
                <li>Create auto-response templates customized to your brand</li>
              </ul>
            </CardBody>
          </Card>
        </Col>

        <Col lg="6" className="mb-4">
          <Card small className="h-100">
            <CardBody>
              <div className="d-flex align-items-center mb-3">
                <FontAwesomeIcon 
                  icon={faExclamationTriangle} 
                  style={{ 
                    fontSize: "24px", 
                    color: "#F9A43F", 
                    marginRight: "12px" 
                  }} 
                />
                <h4 className="mb-0">Prerequisites</h4>
              </div>
              <ul className="pl-4">
                <li className="mb-2">Active EnforcePlus subscription</li>
                <li className="mb-2">Premium or Enterprise tier account</li>
                <li className="mb-2">At least 50 processed disputes in your history</li>
                <li className="mb-2">Administrator privileges</li>
                <li>Brief onboarding call with our team</li>
              </ul>
              <div className="mt-4">
                <Button theme="success" onClick={toggleRequestForm}>
                  Request Access Now
                </Button>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* ClickUp Form Modal */}
      <Modal open={showRequestForm} toggle={toggleRequestForm} size="lg">
        <ModalHeader toggle={toggleRequestForm}>
          Request Dispute Assistant Access
        </ModalHeader>
        <ModalBody className="p-0">
          <iframe 
            className="clickup-embed clickup-dynamic-height" 
            src="https://forms.clickup.com/9014640597/f/8cn0zyn-5334/VM9ARL1RXINAJGRDRK" 
            onWheel={() => {}} 
            width="100%" 
            height="600px" 
            style={{ background: "transparent", border: "none" }}
            title="Request Dispute Assistant Access"
          />
        </ModalBody>
      </Modal>
    </Container>
  );
};

export default DisputeAssistantRequest; 